import type { FC } from 'react';
import NoDataCard from '@/components/NoDataCard';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import Link from '@/components/UI/Link';
import Table from '@/components/UI/Table';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { _isNotEmpty } from '@/littledash';
import type { Approval } from '@/model/Approval.model';
import type { Study } from '@/model/Study.model';
import { statusToTextMap } from '@/referenceData/study/status';
import * as Auth from '@/support/auth';
import { web as webRoute } from '@/support/route';

interface ApprovalsProps {
  approvals?: Array<Approval>;
  study: Study;
}

const Approvals: FC<ApprovalsProps> = ({ approvals, study }) => {
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  const hasWriteAccess = study && Auth.isWriteUserForStudy(study);
  const hasApprovals = _isNotEmpty(approvals);

  const columns: Array<TanStackColumnDef<Approval>> = [
    {
      id: 'approval.name',
      header: 'Name',
      accessorFn: (row: Approval) => row.approval?.name,
      size: 200,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: Approval } }) => {
        const hasDependency = _isNotEmpty(original.outstanding_dependencies);
        return (
          <span
            data-tooltip-id="global-tooltip-id"
            data-tooltip-content={`${hasDependency ? 'Unable to submit until dependency is reviewed' : ''}`}
          >
            {hasWriteAccess ? (
              <Link
                to={webRoute('studies.approvals', { id: study.id, approvalRequestId: original.id })}
                className={`link blue ${hasDependency ? 'ui__disabled' : ''}`}
              >
                {original.approval?.name}
              </Link>
            ) : (
              original.approval?.name
            )}
          </span>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: Approval } }) => statusToTextMap[original.status],
    },
    {
      id: 'outstanding_dependencies',
      header: 'Review dependency',
      accessorKey: 'outstanding_dependencies',
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: Approval } }) =>
        _isNotEmpty(original.outstanding_dependencies)
          ? original.outstanding_dependencies.map((dep) => dep.name).join(', ')
          : '-',
    },
    {
      id: 'approval.submitted_at',
      header: 'Submitted At',
      accessorFn: (row: Approval) => row.submitted_at,
      enableSorting: true,
      cell: ({ row: { original } }: { row: { original: Approval } }) => (
        <DateTimeRenderer value={original.submitted_at} defaultResponse="-" />
      ),
    },
  ];

  return hasApprovals ? (
    <div className="ui-card mb4 overflow-hidden" data-test-component="Approvals" data-test-element="table-container">
      <Table data={approvals} columns={columns} />
    </div>
  ) : (
    <NoDataCard title="There are no reviews required for this study to begin" />
  );
};

export default Approvals;
